import { useState } from "react";
import { AlertTriangle, ArrowDownRight, TrendingDown } from "lucide-react";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { StatCard } from "../../../components/AthenaPricingEngine/stat-card";
import { ExceptionsLogTable } from "../../../components/AthenaPricingEngine/exceptions-log-table";
import { SegmentExceptionsTable } from "../../../components/AthenaPricingEngine/segment-exceptions-table";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { GroupedBarChart } from "../../../components/AthenaPricingEngine/charts/grouped-bar-chart";
import {
  DEAL_EXCEPTIONS_PAGE_SIZE,
  useDealExceptions,
  useSegmentExceptions,
  type DealStatus,
} from "../lib/api/exceptions";

function delta(current: number, previous: number) {
  const diff = current - previous;
  return {
    value: `${diff >= 0 ? "+" : ""}${diff.toLocaleString("en-US")} vs previous period`,
    direction: (diff > 0 ? "up" : diff < 0 ? "down" : "flat") as "up" | "down" | "flat",
  };
}

export default function Exceptions() {
  const [segmentFocus, setSegmentFocus] = useState<{ label: string; nonce: number } | null>(null);
  const [status, setStatus] = useState<DealStatus>("active");
  const [page, setPage] = useState(1);
  const dealsQuery = useDealExceptions(page, DEAL_EXCEPTIONS_PAGE_SIZE, status);
  const segmentsQuery = useSegmentExceptions();

  function handleStatusChange(next: DealStatus) {
    setStatus(next);
    setPage(1);
  }

  const deals = dealsQuery.data?.deals ?? [];
  const total = dealsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / DEAL_EXCEPTIONS_PAGE_SIZE));
  const belowFloorCount = dealsQuery.data?.belowFloorCount;
  const segmentScores = dealsQuery.data?.segmentScores ?? [];
  const worst = dealsQuery.data?.worstShortfall ?? undefined;
  const segments = segmentsQuery.data ?? [];

  const isLoading = dealsQuery.isPending || segmentsQuery.isPending;
  const isError = dealsQuery.isError || segmentsQuery.isError;

  const segmentScoreBars = segmentScores.map((s) => ({
    segment: `${s.office} · ${s.industry}`,
    count: s.count,
  }));
  // Reflects whichever page is currently loaded (page 1 by default) rather
  // than the globally-worst 12 across all ~29,600 deals -- a decorative
  // sparkline, not worth a dedicated backend endpoint just to keep it exact
  // on every page.
  const dealSpark = deals
    .slice(0, 12)
    .map((deal, index) => ({ index, value: Math.abs(deal.shortfall) }));
  const segmentSpark = segments
    .slice(0, 12)
    .map((segment, index) => ({ index, value: segment.floor - segment.blendedRav }));

  return (
    <div>
      <PageHeader title="Exceptions" subtitle="Where price falls below what risk requires" />

      <div className="mt-6 flex justify-end">
        <label className="flex flex-col items-end gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Status
          </span>
          <ToggleGroup
            type="single"
            value={status}
            onValueChange={(value) => value && handleStatusChange(value as DealStatus)}
            className="border border-border bg-secondary"
          >
            <ToggleGroupItem value="active" className="h-8 px-3 text-xs">
              Active
            </ToggleGroupItem>
            <ToggleGroupItem value="closed" className="h-8 px-3 text-xs">
              Closed
            </ToggleGroupItem>
          </ToggleGroup>
        </label>
      </div>

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          Could not load exceptions. Try again in a moment.
        </div>
      ) : isLoading ? (
        <div className="mt-8 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-36 w-full" />
            ))}
          </div>
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <StatCard
              label="Deals Below Floor"
              value={String(total)}
              icon={AlertTriangle}
              sparkline={dealSpark}
              sparkColor="var(--decline)"
              trend={
                belowFloorCount
                  ? { ...delta(belowFloorCount.current, belowFloorCount.previousPeriod), goodDirection: "down" }
                  : undefined
              }
            />
            <StatCard
              label="Segments Trending Below Floor"
              value={String(segments.length)}
              icon={TrendingDown}
              sparkline={segmentSpark}
              sparkColor="var(--amber)"
              trend={{
                value: `${segments.filter((s) => s.monthsBelowFloor >= 6).length} below floor 6+ months`,
                direction: "up",
                goodDirection: "down",
              }}
            />
            <StatCard
              label="Worst Single Shortfall (Net RAV)"
              value={worst ? `${worst.shortfall.toFixed(2)}%` : "—"}
              icon={ArrowDownRight}
              sparkColor="var(--decline)"
              hint={
                worst
                  ? `${worst.dealId} · ${worst.office} / ${worst.industry} · grade ${worst.grade}`
                  : undefined
              }
            />
          </div>

          <Panel
            className="mt-6"
            title="Segment-Level Exceptions"
            description="Segments whose blended RAV has drifted below floor — a different question from any one deal. Select a row for the trend and vintage drag."
          >
            <SegmentExceptionsTable rows={segments} focus={segmentFocus} />
          </Panel>

          <Panel
            className="mt-6"
            title="Exception Count By Segment"
            description="Top 10 (office, industry) segments by exception-deal count, worst first. Select a bar to jump to that segment above."
          >
            <GroupedBarChart
              data={segmentScoreBars}
              categoryKey="segment"
              series={[{ dataKey: "count", name: "Count", color: "var(--decline)" }]}
              suffix=""
              layout="horizontal-bars"
              categoryWidth={220}
              height={Math.max(240, segmentScoreBars.length * 26 + 40)}
              onBarClick={(row) => setSegmentFocus({ label: row.segment, nonce: Date.now() })}
            />
          </Panel>

          <Panel
            className="mt-6"
            title="Deal-Level Exceptions"
            description="Individual deals priced below their required yield, worst shortfall first. Select a row for the pricing breakdown."
          >
            <ExceptionsLogTable
              rows={deals}
              total={total}
              page={page}
              pageCount={pageCount}
              onPageChange={setPage}
              isFetching={dealsQuery.isFetching}
            />
          </Panel>

          <p className="mt-6 text-xs text-muted-foreground">
            Thresholds for what counts an exception are set in Manage Alerts.
          </p>
        </>
      )}
    </div>
  );
}
